// src/components/AmenitiesSection.jsx
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import AmenityTile from './AmenityTile';
import { PublicAPI } from '../api/public';
import { normalizeMediaUrl } from '../api/base';

const Section = styled.section` margin: 20px 0; `;
const H2 = styled.h2` margin: 0 0 12px; `;

/* МЕНЬШЕ КОЛОНОК → КАРТОЧКИ ШИРЕ */
const Grid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  @media (max-width: 1100px){ grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 640px){  grid-template-columns: 1fr; }
`;

function labelFor(a, lang) {
  if (!a) return '';
  
  // Если есть прямые поля name_ru, name_en, name_zh
  if (a.name_ru || a.name_en || a.name_zh) {
    if (lang === 'zh') return a.name_zh || a.name_en || a.name_ru || a.slug;
    if (lang === 'en') return a.name_en || a.name_ru || a.slug;
    return a.name_ru || a.name_en || a.slug;
  }
  
  // Если данные в поле amenity
  const inner = a.amenity || {};
  if (lang === 'zh') return inner.name_zh || inner.name_en || inner.name_ru || inner.slug || '';
  if (lang === 'en') return inner.name_en || inner.name_ru || inner.slug || '';
  return inner.name_ru || inner.name_en || inner.slug || '';
}

function descriptionFor(a, lang) {
  if (!a) return '';
  
  // Если есть прямые поля description_ru, description_en, description_zh
  if (a.description_ru || a.description_en || a.description_zh) {
    if (lang === 'zh') return a.description_zh || a.description_en || a.description_ru || '';
    if (lang === 'en') return a.description_en || a.description_ru || '';
    return a.description_ru || a.description_en || '';
  }
  
  // Если данные в поле amenity
  const inner = a.amenity || {};
  if (lang === 'zh') return inner.description_zh || inner.description_en || inner.description_ru || '';
  if (lang === 'en') return inner.description_en || inner.description_ru || '';
  return inner.description_ru || inner.description_en || '';
}

function coverFor(a) {
  if (!a) return '';
  
  // Приоритет качественным изображениям: сначала cover_url (полный размер), потом cover_thumb
  const url = 
    a.cover_url ||        // полноразмерное изображение (приоритет для качества)
    a.cover_large || 
    a.cover ||
    a.cover_thumb ||      // thumbnail только если нет полноразмерного
    a.image_large ||
    a.image ||
    (a.amenity?.cover_url) ||
    (a.amenity?.cover_large) ||
    (a.amenity?.cover) ||
    (a.amenity?.cover_thumb) || '';
  
  const normalizedUrl = normalizeMediaUrl(url || '');
  
  // Отладочная информация
  // Возвращаем URL только если он действительно существует и не пустой
  return (normalizedUrl && normalizedUrl !== 'null' && normalizedUrl !== 'undefined' && normalizedUrl.trim() !== '') ? normalizedUrl : '';
}

export default function AmenitiesSection({
  slugOrId,
  fallbackAmenities = [],
  titleOverride,
}) {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'ru';

  const [loading, setLoading] = React.useState(false);
  const [items, setItems] = React.useState([]);

  React.useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!slugOrId) { setItems([]); return; }
      setLoading(true);
      try {
        const res = await PublicAPI.propertyAmenities(String(slugOrId), { locale: lang });
        const list = Array.isArray(res?.items) ? res.items : [];
        if (!cancelled) setItems(list);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [slugOrId, lang]);

  const data = items.length ? items : (Array.isArray(fallbackAmenities) ? fallbackAmenities : []);
  if (!data.length && !loading) return null;

  return (
    <Section>
      <H2>{titleOverride || (lang === 'zh' ? '设施' : lang === 'en' ? 'Amenities' : 'Удобства')}</H2>

      {loading && !data.length ? (
        <div style={{opacity:.7}}>{lang === 'zh' ? '加载中…' : lang === 'en' ? 'Loading…' : 'Загрузка…'}</div>
      ) : (
        <Grid>
          {data.map((a, i) => (
              <AmenityTile
              key={String(a.id || a.slug || i)}
              title={labelFor(a, lang)}
              subtitle={descriptionFor(a, lang)}
              image={coverFor(a)}
              icon={a.icon || (a.amenity && a.amenity.icon)}
            />
          ))}
        </Grid>
      )}
    </Section>
  );
}
